import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { Building2, Users, MapPin, Clock, ChevronRight, Thermometer, AlertCircle, CheckCircle, XCircle, PauseCircle, Archive, Copy, Check, ExternalLink, Trash2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SuperadminDashboard = () => {
  const navigate = useNavigate();
  const affiliateRegisterUrl = `${window.location.origin}/affiliate/register`;
  const [stats, setStats] = useState(null);
  const [pendingProductsCount, setPendingProductsCount] = useState(0);
  const [referralReviewCount, setReferralReviewCount] = useState(0);
  const [emailSystemStatus, setEmailSystemStatus] = useState(null);
  const [cleaningMhd, setCleaningMhd] = useState(false);
  const [pushDiag, setPushDiag] = useState(null);
  const [loadingPushDiag, setLoadingPushDiag] = useState(false);

  const handleMhdCleanup = async () => {
    if (!window.confirm('MHD Ghost-Einträge für ALLE Kunden bereinigen?\nDies entfernt alle MHD-Produkte die bereits abgeschrieben wurden und durch den Sync-Bug wieder erstellt wurden.')) return;
    setCleaningMhd(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/superadmin/jobs/cleanup-mhd-ghost-entries`,
        {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`✅ ${res.data.message}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Bereinigen');
    } finally {
      setCleaningMhd(false);
    }
  };

  const handlePushDiagnose = async () => {
    setLoadingPushDiag(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/push/status`, { headers: { Authorization: `Bearer ${token}` } });
      setPushDiag(res.data);
    } catch (e) {
      toast.error('Push-Diagnose fehlgeschlagen');
    } finally {
      setLoadingPushDiag(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchPendingProducts();
    fetchEmailSystemStatus();
    fetchReferralReviewCount();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchPendingProducts = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/pending-products`);
      setPendingProductsCount(response.data.length);
    } catch (error) {
      console.error('Failed to fetch pending products:', error);
    }
  };

  const fetchEmailSystemStatus = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/email-system-status`);
      setEmailSystemStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch email system status:', error);
    }
  };

  const fetchReferralReviewCount = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/referrals/review`);
      setReferralReviewCount(response.data?.count || 0);
    } catch (error) {
      console.error('Failed to fetch referral review count:', error);
    }
  };

  if (!stats) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const statCards = [
    {
      title: 'Standorte',
      value: stats.total_locations,
      icon: MapPin,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      path: '/superadmin/locations',
      description: 'Alle Standorte verwalten'
    },
    {
      title: 'Benachrichtigungen',
      value: stats.unread_notifications || 0,
      icon: Users,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      path: '/superadmin/notifications',
      description: 'Neue Benachrichtigungen'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="dashboard-title">
              Superadmin Dashboard
            </h1>
            <p className="text-muted-foreground mt-2">Willkommen zurück! Hier ist eine Übersicht Ihrer Plattform.</p>
          </div>
          
          {/* Email System Status Badge */}
          {emailSystemStatus && (
            <div className="flex items-center gap-2">
              {emailSystemStatus.status === 'ready' && (
                <Badge className="bg-green-100 text-green-800 border-green-300 flex items-center gap-2 px-3 py-1.5">
                  <CheckCircle className="h-4 w-4" />
                  <span className="font-medium">E-Mail: BEREIT</span>
                </Badge>
              )}
              {emailSystemStatus.status === 'warning' && (
                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 flex items-center gap-2 px-3 py-1.5">
                  <AlertCircle className="h-4 w-4" />
                  <span className="font-medium">E-Mail: TEST-MODUS</span>
                </Badge>
              )}
              {emailSystemStatus.status === 'error' && (
                <Badge className="bg-red-100 text-red-800 border-red-300 flex items-center gap-2 px-3 py-1.5">
                  <XCircle className="h-4 w-4" />
                  <span className="font-medium">E-Mail: FEHLER</span>
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Advanced Customer Analytics - 6 Status Groups */}
        <Card 
          className="cursor-pointer hover:shadow-lg transition-all hover:border-primary group"
          onClick={() => navigate('/superadmin/customers')}
        >
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-600" />
                Erweiterte Kunden-Analyse
              </CardTitle>
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <CardDescription>
              Echtzeit-Übersicht aller Kundenstatusgruppen
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {/* Gesamt */}
              <div className="flex flex-col items-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Building2 className="h-5 w-5 text-blue-600 mb-2" />
                <div className="text-2xl font-bold text-blue-600" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {stats.total_customers}
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-medium">Gesamt</p>
              </div>
              
              {/* Testphase */}
              <div className="flex flex-col items-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <Clock className="h-5 w-5 text-yellow-600 mb-2" />
                <div className="text-2xl font-bold text-yellow-600" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {stats.pending_customers}
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-medium">Testphase</p>
              </div>
              
              {/* Aktiv */}
              <div className="flex flex-col items-center p-4 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="h-5 w-5 text-green-600 mb-2" />
                <div className="text-2xl font-bold text-green-600" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {stats.active_customers}
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-medium">Aktiv</p>
              </div>
              
              {/* Zahlungsproblem (nur tatsächliche Ausfälle) */}
              <div
                className="flex flex-col items-center p-4 bg-orange-50 rounded-lg border border-orange-200 cursor-pointer hover:border-orange-400"
                data-testid="dash-payment-issue"
                onClick={(e) => { e.stopPropagation(); navigate('/superadmin/customers?status=payment_issue'); }}
              >
                <AlertCircle className="h-5 w-5 text-orange-600 mb-2" />
                <div className="text-2xl font-bold text-orange-600" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {stats.payment_issue_customers || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-medium">Zahlungsproblem</p>
              </div>

              {/* Testphase abgelaufen (kein Zahlungsausfall) */}
              <div
                className="flex flex-col items-center p-4 bg-amber-50 rounded-lg border border-amber-200 cursor-pointer hover:border-amber-400"
                data-testid="dash-trial-expired"
                onClick={(e) => { e.stopPropagation(); navigate('/superadmin/customers?status=trial_expired'); }}
              >
                <Clock className="h-5 w-5 text-amber-600 mb-2" />
                <div className="text-2xl font-bold text-amber-600" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {stats.trial_expired_customers || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-medium">Testphase abgelaufen</p>
              </div>
              
              {/* Inaktiv */}
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                <PauseCircle className="h-5 w-5 text-gray-600 mb-2" />
                <div className="text-2xl font-bold text-gray-600" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {stats.inactive_customers || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-medium">Inaktiv</p>
              </div>
              
              {/* Gekündigt */}
              <div className="flex flex-col items-center p-4 bg-gray-100 rounded-lg border border-gray-300">
                <Archive className="h-5 w-5 text-gray-500 mb-2" />
                <div className="text-2xl font-bold text-gray-500" style={{fontFamily: 'Manrope, sans-serif'}}>
                  {stats.canceled_customers || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-1 font-medium">Gekündigt</p>
              </div>
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-muted-foreground">→ Zur detaillierten Kundenverwaltung</p>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={index} 
                data-testid={`stat-card-${index}`}
                className="cursor-pointer hover:shadow-lg transition-all hover:border-primary group"
                onClick={() => navigate(stat.path)}
              >
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                  <div className={`p-2 rounded-md ${stat.bgColor}`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
                    {stat.value}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-muted-foreground">{stat.description}</p>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Affiliate System Quick Links */}
        <Card>
          <CardHeader>
            <CardTitle>🤝 Affiliate-System</CardTitle>
            <CardDescription>
              Verwalten Sie das Partner-Programm und Provisionsstruktur
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Testen und verwalten Sie das Affiliate-System über die folgenden Links:
            </p>
            <div className="grid gap-2">
              {/* Affiliate Partner-Registrierungslink – kopierbar */}
              <div className="border rounded-lg p-3 bg-primary/5 border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-foreground">Dein Partner-Registrierungslink</p>
                  <a
                    href={affiliateRegisterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    Öffnen <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Sende diesen Link an potenzielle Affiliate-Partner – sie können sich direkt selbst registrieren.
                </p>
                <div className="flex items-center gap-2 bg-background border border-border rounded-lg px-3 py-2">
                  <span
                    className="flex-1 text-xs font-mono text-foreground truncate select-all"
                    data-testid="affiliate-register-url"
                  >
                    {affiliateRegisterUrl}
                  </span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(affiliateRegisterUrl);
                      toast.success('Link kopiert!', { description: 'Jetzt an Partner senden.' });
                    }}
                    className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-muted transition-colors flex-shrink-0 border border-border"
                    data-testid="copy-affiliate-link-btn"
                    title="Link kopieren"
                  >
                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                </div>
              </div>

              <div className="p-3 border rounded-lg bg-muted/50">
                <p className="text-sm font-medium mb-1">Affiliate-Dashboard</p>
                <p className="text-xs text-muted-foreground mb-2">Zugriff mit Affiliate-Code:</p>
                <code className="text-xs bg-background px-2 py-1 rounded">
                  /affiliate/dashboard?code=IHR_CODE
                </code>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Product Management */}
        <Card className="cursor-pointer hover:shadow-lg transition-all hover:border-primary group" onClick={() => navigate('/superadmin/products')}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                📦 Produktverwaltung
              </CardTitle>
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <CardDescription>
              Master-Produktdatenbank und CSV-Import
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Verwalten Sie die globale Produktdatenbank und laden Sie CSV-Dateien hoch.
            </p>
            <div className="grid gap-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-green-500"></div>
                <span>CSV-Vorlagen herunterladen</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                <span>Produkte importieren</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-purple-500"></div>
                <span>Import-Historie einsehen</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Products Review */}
        {pendingProductsCount > 0 && (
          <Card className="cursor-pointer hover:shadow-lg transition-all hover:border-orange-500 group border-orange-200 bg-orange-50" onClick={() => navigate('/superadmin/pending-products')}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  Produkt-Freigaben
                  <Badge className="bg-orange-600 text-white">{pendingProductsCount}</Badge>
                </CardTitle>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-orange-600 transition-colors" />
              </div>
              <CardDescription>
                Neue Produkte warten auf Freigabe
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-orange-800">
                Mitarbeiter haben {pendingProductsCount} neue{pendingProductsCount === 1 ? 's' : ''} Produkt{pendingProductsCount !== 1 ? 'e' : ''} im MHD-Shop angelegt.
                Bitte prüfen und freigeben Sie diese für die Master-Datenbank.
              </p>
              <div className="flex items-center gap-2 text-sm font-medium text-orange-700">
                <AlertCircle className="h-4 w-4" />
                <span>Jetzt prüfen →</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empfehlungs-Prüffälle (Freunde werben Freunde) */}
        <Card
          data-testid="referral-review-card"
          className={`cursor-pointer hover:shadow-lg transition-all group ${referralReviewCount > 0 ? 'border-orange-300 bg-orange-50 dark:bg-orange-950/10 hover:border-orange-500' : 'hover:border-primary'}`}
          onClick={() => navigate('/superadmin/referral-review')}
        >
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className={`h-5 w-5 ${referralReviewCount > 0 ? 'text-orange-600' : 'text-muted-foreground'}`} />
                Empfehlungs-Prüffälle
                {referralReviewCount > 0 && <Badge className="bg-orange-600 text-white">{referralReviewCount}</Badge>}
              </CardTitle>
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <CardDescription>
              Unklare „Freunde werben Freunde“-Dubletten begründet entscheiden (40 € Rabatt inkl. USt freigeben/ablehnen)
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Push-Benachrichtigung Diagnose */}
        <Card className="border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-blue-800 dark:text-blue-300">
              🔔 Push-Benachrichtigung Diagnose
            </CardTitle>
            <CardDescription className="text-blue-700 dark:text-blue-400">
              Prüft ob VAPID-Keys korrekt konfiguriert sind und wie viele Geräte Push-Benachrichtigungen empfangen können.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={handlePushDiagnose}
              disabled={loadingPushDiag}
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loadingPushDiag ? <Loader2 className="h-4 w-4 animate-spin" /> : '🔍'}
              Push-Status prüfen
            </Button>
            {pushDiag && (
              <div className={`rounded-xl p-4 space-y-2 text-sm border ${
                pushDiag.diagnosis === 'OK' ? 'bg-green-50 border-green-200 text-green-800' :
                pushDiag.diagnosis === 'VAPID_KEY_MISSING' ? 'bg-red-50 border-red-200 text-red-800' :
                'bg-amber-50 border-amber-200 text-amber-800'
              }`}>
                <div className="flex items-center gap-2 font-bold">
                  {pushDiag.diagnosis === 'OK' ? '✅' : pushDiag.diagnosis === 'VAPID_KEY_MISSING' ? '❌' : '⚠️'}
                  {pushDiag.diagnosis === 'OK' ? 'Push funktioniert korrekt' :
                   pushDiag.diagnosis === 'VAPID_KEY_MISSING' ? 'VAPID-Key fehlt – Push KOMPLETT DEAKTIVIERT' :
                   'Keine Subscription – Push-Erlaubnis fehlt'}
                </div>
                <div className="space-y-1 text-xs">
                  <p>VAPID-Key konfiguriert: <strong>{pushDiag.vapid_configured ? 'Ja ✅' : 'Nein ❌'}</strong></p>
                  <p>Deine aktiven Geräte: <strong>{pushDiag.count}</strong></p>
                  {pushDiag.total_active_in_team > 0 && (
                    <p>Aktive Geräte im Team: <strong>{pushDiag.total_active_in_team}</strong></p>
                  )}
                  {pushDiag.diagnosis === 'VAPID_KEY_MISSING' && (
                    <p className="mt-2 font-semibold">→ Bitte IONOS deployen und dann neu prüfen. Nach Deploy: alle Mitarbeiter müssen Push-Erlaubnis neu erteilen.</p>
                  )}
                  {pushDiag.diagnosis === 'NO_SUBSCRIPTION' && (
                    <p className="mt-2 font-semibold">→ In der App unter Einstellungen → Push-Benachrichtigungen aktivieren.</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* MHD Ghost-Einträge bereinigen */}
        <Card className="border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-red-800 dark:text-red-300">
              <Trash2 className="h-5 w-5" />
              MHD Ghost-Einträge bereinigen
            </CardTitle>
            <CardDescription className="text-red-700 dark:text-red-400">
              Entfernt MHD-Produkte die bereits abgeschrieben wurden, aber durch den Lieferungs-Sync erneut erstellt wurden.
              Betrifft alle Kunden. Einmalig nach dem Deployment ausführen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleMhdCleanup}
              disabled={cleaningMhd}
              className="gap-2 bg-red-600 hover:bg-red-700 text-white"
              data-testid="mhd-cleanup-btn"
            >
              {cleaningMhd ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {cleaningMhd ? 'Bereinigt...' : 'Ghost-Einträge jetzt bereinigen'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Willkommen bei AllFlowX</CardTitle>
            <CardDescription>
              Ihre Multi-Tenant SaaS-Plattform für Betriebs- und Mitarbeiterorganisation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Als Superadmin haben Sie <strong>vollen Zugriff</strong> auf alle Kunden, Standorte, Benutzer und Einstellungen.
              Klicken Sie auf eine Kachel oben, um zur entsprechenden Verwaltung zu gelangen.
            </p>
            <div className="border-l-4 border-primary pl-4 py-2">
              <p className="text-sm font-medium">Hinweis:</p>
              <p className="text-sm text-muted-foreground">
                Neue Kunden-Registrierungen werden automatisch mit Status "PENDING" angelegt.
                Sie müssen diese manuell freischalten, bevor die Kunden sich anmelden können.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
